/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * Platform Registry
 * Central registry for managing DAS platform configurations
 * Provides a single point to register and retrieve platform-specific implementations
 */

import { PlatformConfig } from "./PlatformConfig";
import { MA3000PlatformConfig } from "./MA3000PlatformConfig";
import { MA6200G1PlatformConfig } from "./MA6200G1PlatformConfig";
import { MA6000G2PlatformConfig } from "./MA6000G2PlatformConfig";
import { MA6200G2PlatformConfig } from "./MA6200G2PlatformConfig";

class PlatformRegistry {
  private platforms: Map<string, PlatformConfig> = new Map();
  private defaultPlatform: PlatformConfig;

  constructor() {
    // Register built-in platforms
    this.registerPlatform(new MA3000PlatformConfig());
    this.registerPlatform(new MA6200G1PlatformConfig());
    this.registerPlatform(new MA6000G2PlatformConfig());
    this.registerPlatform(new MA6200G2PlatformConfig());
    
    // Set MA3000 as default for backward compatibility
    this.defaultPlatform = this.platforms.get("ma3000")!;
    
    console.log("[PlatformRegistry] Initialized with platforms:", Array.from(this.platforms.keys()));
  }

  /**
   * Register a new platform configuration
   * @param config Platform configuration to register
   */
  registerPlatform(config: PlatformConfig): void {
    if (this.platforms.has(config.platformId)) {
      console.warn(`[PlatformRegistry] Platform ${config.platformId} is already registered. Overwriting.`);
    }
    this.platforms.set(config.platformId, config);
    console.log(`[PlatformRegistry] Registered platform: ${config.platformId} (${config.displayName})`);
  }

  /**
   * Get platform configuration by ID
   * Supports flexible matching (e.g., "ma6200-g1" matches "ma6200g1")
   * @param platformId Platform identifier
   * @returns Platform configuration, or default if not found
   */
  getPlatform(platformId?: string): PlatformConfig {
    if (!platformId) {
      console.log("[PlatformRegistry] No platformId provided, using default:", this.defaultPlatform.platformId);
      return this.defaultPlatform;
    }

    // Try direct lookup first
    const directMatch = this.platforms.get(platformId.toLowerCase());
    if (directMatch) {
      console.log("[PlatformRegistry] Direct match found:", directMatch.platformId);
      return directMatch;
    }

    // Try flexible matching using platform's matches() method
    const platformsArray = Array.from(this.platforms.values());
    for (const config of platformsArray) {
      if (config.matches(platformId)) {
        console.log(`[PlatformRegistry] Matched platform: ${platformId} -> ${config.platformId}`);
        return config;
      }
    }

    // Fallback to default
    console.warn(`[PlatformRegistry] Unknown platform: ${platformId}. Using default: ${this.defaultPlatform.platformId}`);
    return this.defaultPlatform;
  }

  /**
   * Get all registered platforms
   */
  getAllPlatforms(): PlatformConfig[] {
    return Array.from(this.platforms.values());
  }

  /**
   * Check if a platform is registered
   */
  hasPlatform(platformId: string): boolean {
    if (this.platforms.has(platformId.toLowerCase())) {
      return true;
    }
    const platformsArray = Array.from(this.platforms.values());
    return platformsArray.some(p => p.matches(platformId));
  }

  /**
   * Set the default platform
   */
  setDefaultPlatform(platformId: string): void {
    const platform = this.getPlatform(platformId);
    if (platform) {
      this.defaultPlatform = platform;
      console.log(`[PlatformRegistry] Default platform set to: ${platform.platformId}`);
    }
  }
}

// Export singleton instance
export const platformRegistry = new PlatformRegistry();

// Export class for testing or custom instances
export { PlatformRegistry };
